import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { adminMessagesService } from '../../../services/profileAnalyticsService';
import { supabase } from '../../../lib/supabase';

const SENDER_TYPES = [
  { value: 'club', label: 'Club Messages', icon: 'Building2' },
  { value: 'scout', label: 'Scout Messages', icon: 'Search' },
  { value: 'sports_director', label: 'Sports Directors', icon: 'Award' },
  { value: 'fifa_agent', label: 'FIFA Agents', icon: 'Briefcase' },
  { value: 'club_scouter', label: 'Club Scouters', icon: 'Users' },
];

const TYPE_COLORS = {
  club: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  scout: 'bg-green-500/10 text-green-500 border-green-500/30',
  sports_director: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  fifa_agent: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  club_scouter: 'bg-rose-500/10 text-rose-500 border-rose-500/30',
};

const TYPE_ICON_COLORS = {
  club: 'text-blue-500',
  scout: 'text-green-500',
  sports_director: 'text-purple-500',
  fifa_agent: 'text-amber-500',
  club_scouter: 'text-rose-500',
};

const EMPTY_FORM = {
  playerId: '',
  userId: '',
  senderType: '',
  // club fields
  clubName: '',
  clubCountry: '',
  clubLeague: '',
  // scout fields
  scoutName: '',
  scoutCountry: '',
  // sports director fields
  directorName: '',
  directorClubs: '',
  // fifa agent fields
  agentName: '',
  agentAgency: '',
  agentCountry: '',
  // club scouter fields
  scouterName: '',
  scouterClub: '',
  scouterCountry: '',
  message: '',
};

const AdminMessageManager = ({ onClose }) => {
  const [step, setStep] = useState(1); // 1=select player, 2=select type, 3=compose
  const [players, setPlayers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewMode, setViewMode] = useState('compose'); // 'compose' | 'sent'
  const [filterPlayer, setFilterPlayer] = useState('');

  const selectedPlayer = players?.find(p => p?.id === formData?.playerId);

  useEffect(() => {
    fetchPlayers();
    fetchMessages();
  }, []);

  const fetchPlayers = async () => {
    try {
      const { data, error } = await supabase
        ?.from('player_master_profiles')
        ?.select('id, full_name, primary_position')
        ?.order('full_name');
      if (error) throw error;

      const { data: userProfiles } = await supabase
        ?.from('user_profiles')
        ?.select('id, assigned_profile_id')
        ?.not('assigned_profile_id', 'is', null);

      const profileMap = {};
      userProfiles?.forEach(up => { profileMap[up?.assigned_profile_id] = up?.id; });

      setPlayers((data || [])?.map(p => ({ ...p, userId: profileMap?.[p?.id] || null })));
    } catch (err) {
      console.error('Error fetching players:', err);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await adminMessagesService?.getAll();
      setMessages(response?.data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildSenderFields = () => {
    const { senderType } = formData;
    const fields = {};

    if (senderType === 'club') {
      fields.senderName = formData?.clubName;
      fields.senderOrganization = formData?.clubName;
      fields.senderCountry = formData?.clubCountry;
      fields.senderLeague = formData?.clubLeague;
    } else if (senderType === 'scout') {
      fields.senderName = formData?.scoutName;
      fields.senderCountry = formData?.scoutCountry;
    } else if (senderType === 'sports_director') {
      fields.senderName = formData?.directorName;
      fields.senderOrganization = formData?.directorClubs;
    } else if (senderType === 'fifa_agent') {
      fields.senderName = formData?.agentName;
      fields.senderOrganization = formData?.agentAgency;
      fields.senderCountry = formData?.agentCountry;
    } else if (senderType === 'club_scouter') {
      fields.senderName = formData?.scouterName;
      fields.senderOrganization = formData?.scouterClub;
      fields.senderCountry = formData?.scouterCountry;
    }

    return fields;
  };

  const handleSelectPlayer = (value) => {
    const player = players?.find(p => p?.id === value);
    setFormData(prev => ({
      ...prev,
      playerId: value,
      userId: player?.userId || null
    }));
    setStep(2);
  };

  const handleSelectType = (type) => {
    setFormData(prev => ({ ...prev, senderType: type }));
    setStep(3);
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    setSending(true);
    try {
      const senderFields = buildSenderFields();

      // Auto-generate subject based on sender type
      let subject = '';
      if (formData?.senderType === 'club') {
        subject = `Message from ${formData?.clubName || 'Club'}${formData?.clubLeague ? ` (${formData?.clubLeague})` : ''}`;
      } else if (formData?.senderType === 'scout') {
        subject = `Message from Scout: ${formData?.scoutName || 'Scout'}${formData?.scoutCountry ? `, ${formData?.scoutCountry}` : ''}`;
      } else if (formData?.senderType === 'sports_director') {
        subject = `Message from Sports Director: ${formData?.directorName || 'Sports Director'}`;
      } else if (formData?.senderType === 'fifa_agent') {
        subject = `Message from FIFA Agent: ${formData?.agentName || 'FIFA Agent'}${formData?.agentAgency ? ` (${formData?.agentAgency})` : ''}`;
      } else if (formData?.senderType === 'club_scouter') {
        subject = `Message from Club Scouter: ${formData?.scouterName || 'Club Scouter'}${formData?.scouterClub ? ` - ${formData?.scouterClub}` : ''}`;
      } else {
        subject = 'New Message';
      }

      await adminMessagesService?.create({
        playerId: formData?.playerId,
        userId: formData?.userId,
        senderType: formData?.senderType,
        subject,
        message: formData?.message,
        priority: 'normal',
        ...senderFields,
      });
      setFormData(EMPTY_FORM);
      setStep(1);
      setViewMode('sent');
      fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message: ' + err?.message);
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (msgId) => {
    if (!confirm('Delete this message?')) return;
    try {
      await adminMessagesService?.delete(msgId);
      if (selectedMessage?.id === msgId) setSelectedMessage(null);
      fetchMessages();
    } catch (err) {
      alert('Failed to delete: ' + err?.message);
    }
  };

  const getSenderIcon = (type) => SENDER_TYPES?.find(t => t?.value === type)?.icon || 'Mail';
  const getSenderLabel = (type) => SENDER_TYPES?.find(t => t?.value === type)?.label || type;

  const playerOptions = players?.map(p => ({
    value: p?.id,
    label: `${p?.full_name}${p?.primary_position ? ` (${p?.primary_position})` : ''}`
  }));

  const filteredMessages = filterPlayer
    ? messages?.filter(m => m?.player?.full_name?.toLowerCase()?.includes(filterPlayer?.toLowerCase()))
    : messages;

  // Group sent messages by player for display
  const messagesByPlayer = {};
  filteredMessages?.forEach(m => {
    const name = m?.player?.full_name || 'Unknown';
    if (!messagesByPlayer?.[name]) messagesByPlayer[name] = [];
    messagesByPlayer?.[name]?.push(m);
  });

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString() : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="MessageSquare" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground font-headline">Message Center</h2>
              <p className="text-xs text-muted-foreground">Send professional messages to players</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('compose')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                viewMode === 'compose' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              Compose
            </button>
            <button
              onClick={() => setViewMode('sent')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                viewMode === 'sent' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              Sent ({messages?.length})
            </button>
            <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* ===== COMPOSE VIEW ===== */}
          {viewMode === 'compose' && (
            <div className="p-6">
              {/* Step Indicator */}
              <div className="flex items-center gap-2 mb-8">
                {[{n:1,label:'Select Player'},{n:2,label:'Message Type'},{n:3,label:'Compose'}]?.map((s, i) => (
                  <React.Fragment key={s?.n}>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        step > s?.n ? 'bg-primary text-primary-foreground' :
                        step === s?.n ? 'bg-primary text-primary-foreground ring-4 ring-primary/20': 'bg-muted text-muted-foreground'
                      }`}>
                        {step > s?.n ? <Icon name="Check" size={14} /> : s?.n}
                      </div>
                      <span className={`text-sm font-medium hidden sm:block ${
                        step >= s?.n ? 'text-foreground' : 'text-muted-foreground'
                      }`}>{s?.label}</span>
                    </div>
                    {i < 2 && <div className={`flex-1 h-0.5 ${ step > s?.n ? 'bg-primary' : 'bg-border'}`} />}
                  </React.Fragment>
                ))}
              </div>

              {/* STEP 1: Select Player */}
              {step === 1 && (
                <div className="max-w-lg mx-auto">
                  <h3 className="text-lg font-bold text-foreground mb-2 font-headline">Who is this message for?</h3>
                  <p className="text-sm text-muted-foreground mb-6">Select the player who will receive this message in their dashboard.</p>
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-foreground mb-2">Select Player</label>
                    <Select
                      value={formData?.playerId}
                      onChange={handleSelectPlayer}
                      options={playerOptions}
                      placeholder="Search and select a player..."
                      searchable
                    />
                  </div>
                  {players?.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Users" size={40} className="mx-auto mb-3 opacity-40" />
                      <p>No players found</p>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: Select Sender Type */}
              {step === 2 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => setStep(1)} className="text-muted-foreground hover:text-foreground transition-colors">
                      <Icon name="ArrowLeft" size={20} />
                    </button>
                    <div>
                      <h3 className="text-lg font-bold text-foreground font-headline">Choose Message Type</h3>
                      <p className="text-sm text-muted-foreground">Sending to: <span className="font-semibold text-foreground">{selectedPlayer?.full_name}</span></p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {SENDER_TYPES?.map(type => (
                      <button
                        key={type?.value}
                        onClick={() => handleSelectType(type?.value)}
                        className={`group p-5 rounded-xl border-2 text-left transition-all hover:scale-[1.02] hover:shadow-lg ${
                          TYPE_COLORS?.[type?.value] || 'border-border'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                          TYPE_COLORS?.[type?.value]?.split(' ')?.[0] || 'bg-muted'
                        }`}>
                          <Icon name={type?.icon} size={24} className={TYPE_ICON_COLORS?.[type?.value]} />
                        </div>
                        <p className="font-bold text-foreground text-base">{type?.label}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {type?.value === 'club' && 'Club name, country, league'}
                          {type?.value === 'scout' && 'Scout name, country'}
                          {type?.value === 'sports_director' && 'Name, clubs represented'}
                          {type?.value === 'fifa_agent' && 'Name, agency, country'}
                          {type?.value === 'club_scouter' && 'Name, club, country'}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 3: Compose */}
              {step === 3 && (
                <form onSubmit={handleSend}>
                  <div className="flex items-center gap-3 mb-6">
                    <button type="button" onClick={() => setStep(2)} className="text-muted-foreground hover:text-foreground transition-colors">
                      <Icon name="ArrowLeft" size={20} />
                    </button>
                    <div>
                      <h3 className="text-lg font-bold text-foreground font-headline">Compose Message</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-muted-foreground">To: <span className="font-semibold text-foreground">{selectedPlayer?.full_name}</span></span>
                        <span className="text-muted-foreground">•</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                          TYPE_COLORS?.[formData?.senderType]
                        }`}>
                          {getSenderLabel(formData?.senderType)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                    {/* Club Fields */}
                    {formData?.senderType === 'club' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Club Name *</label>
                          <Input value={formData?.clubName} onChange={e => setFormData(p => ({...p, clubName: e?.target?.value}))} placeholder="e.g. AC Milan" required />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Country *</label>
                          <Input value={formData?.clubCountry} onChange={e => setFormData(p => ({...p, clubCountry: e?.target?.value}))} placeholder="e.g. Italy" required />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-foreground mb-2">League *</label>
                          <Input value={formData?.clubLeague} onChange={e => setFormData(p => ({...p, clubLeague: e?.target?.value}))} placeholder="e.g. Serie A" required />
                        </div>
                      </>
                    )}

                    {/* Scout Fields */}
                    {formData?.senderType === 'scout' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Scout Name *</label>
                          <Input value={formData?.scoutName} onChange={e => setFormData(p => ({...p, scoutName: e?.target?.value}))} placeholder="e.g. John Smith" required />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Country *</label>
                          <Input value={formData?.scoutCountry} onChange={e => setFormData(p => ({...p, scoutCountry: e?.target?.value}))} placeholder="e.g. England" required />
                        </div>
                      </>
                    )}

                    {/* Sports Director Fields */}
                    {formData?.senderType === 'sports_director' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Director Name *</label>
                          <Input value={formData?.directorName} onChange={e => setFormData(p => ({...p, directorName: e?.target?.value}))} placeholder="e.g. Paolo Maldini" required />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Clubs Represented *</label>
                          <Input value={formData?.directorClubs} onChange={e => setFormData(p => ({...p, directorClubs: e?.target?.value}))} placeholder="e.g. AC Milan" required />
                        </div>
                      </>
                    )}

                    {/* FIFA Agent Fields */}
                    {formData?.senderType === 'fifa_agent' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Agent Name *</label>
                          <Input value={formData?.agentName} onChange={e => setFormData(p => ({...p, agentName: e?.target?.value}))} placeholder="e.g. Jorge Mendes" required />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Agency *</label>
                          <Input value={formData?.agentAgency} onChange={e => setFormData(p => ({...p, agentAgency: e?.target?.value}))} placeholder="e.g. Gestifute" required />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-foreground mb-2">Country *</label>
                          <Input value={formData?.agentCountry} onChange={e => setFormData(p => ({...p, agentCountry: e?.target?.value}))} placeholder="e.g. Portugal" required />
                        </div>
                      </>
                    )}

                    {/* Club Scouter Fields */}
                    {formData?.senderType === 'club_scouter' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Scouter Name *</label>
                          <Input value={formData?.scouterName} onChange={e => setFormData(p => ({...p, scouterName: e?.target?.value}))} placeholder="e.g. David Taylor" required />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-2">Club *</label>
                          <Input value={formData?.scouterClub} onChange={e => setFormData(p => ({...p, scouterClub: e?.target?.value}))} placeholder="e.g. Manchester City" required />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-foreground mb-2">Country *</label>
                          <Input value={formData?.scouterCountry} onChange={e => setFormData(p => ({...p, scouterCountry: e?.target?.value}))} placeholder="e.g. England" required />
                        </div>
                      </>
                    )}

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-foreground mb-2">Message *</label>
                      <textarea
                        value={formData?.message}
                        onChange={e => setFormData(p => ({...p, message: e?.target?.value}))}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
                        placeholder="Write your message here..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => { setFormData(EMPTY_FORM); setStep(1); }}>Reset</Button>
                    <Button type="submit" variant="default" disabled={sending} iconName={sending ? undefined : 'Send'} iconPosition="left">
                      {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* ===== SENT MESSAGES VIEW ===== */}
          {viewMode === 'sent' && (
            <div className="flex h-full" style={{minHeight: '500px'}}>
              {/* Left: Messages List */}
              <div className="w-full lg:w-2/5 border-r border-border flex flex-col">
                <div className="p-4 border-b border-border">
                  <Input
                    type="text"
                    value={filterPlayer}
                    onChange={e => setFilterPlayer(e?.target?.value)}
                    placeholder="Filter by player name..."
                    className="text-sm"
                  />
                </div>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredMessages?.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Icon name="Inbox" size={40} className="mx-auto mb-3 opacity-40" />
                    <p className="text-sm">No messages sent yet</p>
                  </div>
                ) : (
                  <div className="overflow-y-auto flex-1 divide-y divide-border">
                    {filteredMessages?.map(msg => (
                      <button
                        key={msg?.id}
                        onClick={() => setSelectedMessage(msg)}
                        className={`w-full text-left p-4 hover:bg-muted transition-colors ${
                          selectedMessage?.id === msg?.id ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            TYPE_COLORS?.[msg?.senderType]?.split(' ')?.[0] || 'bg-muted'
                          }`}>
                            <Icon name={getSenderIcon(msg?.senderType)} size={16} className={TYPE_ICON_COLORS?.[msg?.senderType]} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">{msg?.player?.full_name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground truncate">{msg?.senderName} • {getSenderLabel(msg?.senderType)?.replace(' Messages','')}</p>
                            <p className="text-xs text-muted-foreground mt-1">{formatDate(msg?.createdAt)}</p>
                            {msg?.playerReply && (
                              <span className="inline-flex items-center gap-1 mt-1 text-xs text-primary font-semibold">
                                <Icon name="Reply" size={10} /> Player replied
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Message Detail */}
              <div className="hidden lg:flex flex-col flex-1">
                {selectedMessage ? (
                  <>
                    <div className="p-5 border-b border-border">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                            TYPE_COLORS?.[selectedMessage?.senderType]?.split(' ')?.[0] || 'bg-muted'
                          }`}>
                            <Icon name={getSenderIcon(selectedMessage?.senderType)} size={22} className={TYPE_ICON_COLORS?.[selectedMessage?.senderType]} />
                          </div>
                          <div>
                            <p className="font-bold text-foreground">{selectedMessage?.senderName}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                              TYPE_COLORS?.[selectedMessage?.senderType]
                            }`}>
                              {getSenderLabel(selectedMessage?.senderType)}
                            </span>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" iconName="Trash2" className="text-error hover:text-error" onClick={() => handleDelete(selectedMessage?.id)} />
                      </div>
                      {/* Sender Details */}
                      <div className="bg-muted/50 rounded-lg p-3 space-y-1.5 text-sm">
                        <p className="font-semibold text-foreground">To: {selectedMessage?.player?.full_name}</p>
                        {selectedMessage?.senderOrganization && <p className="text-muted-foreground">Organization: <span className="text-foreground">{selectedMessage?.senderOrganization}</span></p>}
                        {selectedMessage?.senderCountry && <p className="text-muted-foreground">Country: <span className="text-foreground">{selectedMessage?.senderCountry}</span></p>}
                        {selectedMessage?.senderLeague && <p className="text-muted-foreground">League: <span className="text-foreground">{selectedMessage?.senderLeague}</span></p>}
                        <p className="text-muted-foreground">Sent: <span className="text-foreground">{formatDate(selectedMessage?.createdAt)}</span></p>
                      </div>
                    </div>
                    <div className="p-5 flex-1 overflow-y-auto">
                      <h4 className="font-bold text-foreground mb-3 font-headline">{selectedMessage?.subject}</h4>
                      <p className="text-foreground whitespace-pre-line text-sm leading-relaxed">{selectedMessage?.message}</p>

                      {/* Player Reply */}
                      {selectedMessage?.playerReply && (
                        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="Reply" size={16} className="text-primary" />
                            <p className="text-sm font-bold text-primary">Player's Reply</p>
                            <span className="text-xs text-muted-foreground ml-auto">{formatDate(selectedMessage?.playerReplyAt)}</span>
                          </div>
                          <p className="text-sm text-foreground whitespace-pre-line">{selectedMessage?.playerReply}</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-muted-foreground">
                      <Icon name="MessageSquare" size={48} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Select a message to view details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessageManager;